import { supabase } from '@/config/supabase';
import { logAudit } from './auditLog';
import { getViewingOrgId } from '@/utils/effectiveOrgId';
import type { DecisionApproval } from '@/types';

export interface PendingApprovalRow {
  id: string;
  decision_check_id: string;
  level_number: number;
  level_name: string | null;
  status: string;
  due_at: string | null;
  reference_number: string | null;
  decision_title: string;
  estimated_value: number;
  currency: string;
  urgency: string;
  decision_created_at: string;
  requester_name: string | null;
}

/** Dashboard stat: count of approvals assigned to the signed-in member and still pending (FR-010, FR-011). */
export async function getMyPendingCount(): Promise<number> {
  const { count, error } = await supabase
    .from('my_pending_approvals')
    .select('id', { count: 'exact', head: true });

  if (error) throw new Error(error.message);
  return count ?? 0;
}

/**
 * Dashboard: approvals assigned to the signed-in member, via the
 * `my_pending_approvals` view (0001_initial_schema.sql §14.2), which already
 * joins decision_checks + requester name and is RLS-scoped to the caller —
 * reference, title, requester, value, urgency, submitted date (FR-011).
 */
export async function listMyPendingApprovals(limit = 5): Promise<PendingApprovalRow[]> {
  const { data, error } = await supabase
    .from('my_pending_approvals')
    .select('*')
    .order('decision_created_at', { ascending: false })
    .limit(limit);

  if (error) throw new Error(error.message);
  return (data ?? []) as PendingApprovalRow[];
}

/**
 * The full approval chain for one Decision Check, ordered by level — used
 * to render the read-only ApprovalTimeline on the Decision Detail page
 * (FR-034, spec Scenario 8). Approve/reject/return actions are added in
 * User Story 4 (T093); this story only needs to display the chain.
 */
export async function getChainForDecision(decisionCheckId: string): Promise<DecisionApproval[]> {
  const { data, error } = await supabase
    .from('decision_approvals')
    .select('*')
    .eq('decision_check_id', decisionCheckId)
    .order('level_number', { ascending: true });

  if (error) throw new Error(error.message);
  return (data ?? []) as DecisionApproval[];
}

/** "My Approvals" full list — approvals assigned to the signed-in member, any status (US4). RLS restricts this to their own assignments unless they're a Manager+. */
export async function listMinePending(): Promise<PendingApprovalRow[]> {
  const { data, error } = await supabase.from('my_pending_approvals').select('*').order('decision_created_at', { ascending: false });
  if (error) throw new Error(error.message);
  return (data ?? []) as PendingApprovalRow[];
}

/** All pending approvals org-wide — only returns rows for Manager/Admin/Owner callers per RLS (FR-011). */
export async function listAllPending(): Promise<DecisionApproval[]> {
  let query = supabase.from('decision_approvals').select('*').eq('status', 'pending').order('created_at', { ascending: false });
  const viewingOrgId = getViewingOrgId();
  if (viewingOrgId) query = query.eq('organisation_id', viewingOrgId);

  const { data, error } = await query;
  if (error) throw new Error(error.message);
  return (data ?? []) as DecisionApproval[];
}

async function respondToApproval(id: string, decisionCheckId: string, status: 'approved' | 'rejected' | 'returned', comments: string) {
  const { error } = await supabase
    .from('decision_approvals')
    .update({ status, responded_at: new Date().toISOString(), response_comments: comments || null })
    .eq('id', id)
    .eq('status', 'pending');

  if (error) throw new Error(error.message);
  await logAudit({ action: 'update', resource_type: 'decision_approval', resource_id: id, description: `Approval ${status}: ${comments}` });

  // Advance/complete/halt the parent Decision Check's overall status.
  if (status === 'rejected' || status === 'returned') {
    await supabase.from('decision_checks').update({ status: status === 'rejected' ? 'rejected' : 'pending_approval' }).eq('id', decisionCheckId);
    return;
  }

  const { data: chain } = await supabase.from('decision_approvals').select('status').eq('decision_check_id', decisionCheckId);
  const allApproved = (chain ?? []).every((step) => step.status === 'approved');
  if (allApproved) {
    await supabase.from('decision_checks').update({ status: 'approved', completed_at: new Date().toISOString() }).eq('id', decisionCheckId);
  }
}

/** Approve action — requires the "I confirm I have authority to approve" acknowledgement to already be true by the time this is called (FR-051). */
export async function approveDecisionApproval(id: string, decisionCheckId: string, comments = ''): Promise<void> {
  await respondToApproval(id, decisionCheckId, 'approved', comments);
}

/** Reject action — `reason` is required and enforced non-empty by the caller's form validation (FR-052). */
export async function rejectDecisionApproval(id: string, decisionCheckId: string, reason: string): Promise<void> {
  await respondToApproval(id, decisionCheckId, 'rejected', reason);
}

/** Return-for-revision action — `description` of what needs to change is required (FR-052). */
export async function returnDecisionApproval(id: string, decisionCheckId: string, description: string): Promise<void> {
  await respondToApproval(id, decisionCheckId, 'returned', description);
}
